import type { ProductRedemptionType } from '@/shared/types/product';
import type { ProductFormPayload } from '../api/use-products';

const CSV_HEADER = ['sku', 'nome', 'valor', 'preco', 'resgate', 'tipo_resgate'];

const REDEMPTION_TYPE_BY_LABEL: Record<string, ProductRedemptionType> = {
  produto: 'product',
  desconto: 'discount',
  cortesia: 'courtesy',
  aniversario: 'birthday',
  aniversário: 'birthday',
};

function csvEscape(value: string | number): string {
  const s = String(value ?? '');
  if (s.includes('"') || s.includes(',') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Baixa o modelo CSV pra preencher e reimportar. */
export function downloadProductTemplate(): void {
  const rows = [CSV_HEADER, ['ABC123', 'Produto exemplo', '10', '0', 'sim', 'produto']];
  const csv = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'modelo-produtos.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/** Parser simples com suporte a campos entre aspas (CSV básico, sem dependência nova). */
function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      cells.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  cells.push(cur);
  return cells;
}

export interface ParsedProductRow {
  line: number;
  payload: Omit<ProductFormPayload, 'action' | 'id' | 'idPassbook'>;
}

export interface ProductCsvParseError {
  line: number;
  message: string;
}

export interface ProductCsvParseResult {
  rows: ParsedProductRow[];
  errors: ProductCsvParseError[];
}

export function parseProductsCsv(text: string): ProductCsvParseResult {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '');
  const rows: ParsedProductRow[] = [];
  const errors: ProductCsvParseError[] = [];

  if (lines.length === 0) {
    return { rows, errors: [{ line: 0, message: 'Arquivo vazio.' }] };
  }

  const header = parseCsvLine(lines[0]).map((c) => c.trim().toLowerCase());
  const idx = {
    sku: header.indexOf('sku'),
    nome: header.indexOf('nome'),
    valor: header.indexOf('valor'),
    preco: header.indexOf('preco'),
    resgate: header.indexOf('resgate'),
    tipoResgate: header.indexOf('tipo_resgate'),
  };
  if (idx.sku === -1 || idx.nome === -1 || idx.valor === -1) {
    return {
      rows,
      errors: [{ line: 1, message: 'Cabeçalho inválido — esperado ao menos: sku, nome, valor.' }],
    };
  }

  for (let i = 1; i < lines.length; i++) {
    const lineNumber = i + 1;
    const cells = parseCsvLine(lines[i]);
    const sku = (cells[idx.sku] ?? '').trim();
    const nome = (cells[idx.nome] ?? '').trim();
    const valorRaw = (cells[idx.valor] ?? '').trim();
    const precoRaw = idx.preco !== -1 ? (cells[idx.preco] ?? '').trim() : '';
    const resgateRaw = idx.resgate !== -1 ? (cells[idx.resgate] ?? '').trim().toLowerCase() : '';
    const tipoRaw = idx.tipoResgate !== -1 ? (cells[idx.tipoResgate] ?? '').trim().toLowerCase() : '';

    if (!sku) {
      errors.push({ line: lineNumber, message: 'SKU obrigatório.' });
      continue;
    }
    if (!nome) {
      errors.push({ line: lineNumber, message: 'Nome obrigatório.' });
      continue;
    }
    const valor = Number(valorRaw.replace(',', '.'));
    if (!Number.isFinite(valor) || valor < 0) {
      errors.push({ line: lineNumber, message: `Valor inválido: "${valorRaw}".` });
      continue;
    }
    const redemptionType = tipoRaw ? REDEMPTION_TYPE_BY_LABEL[tipoRaw] : undefined;
    if (tipoRaw && !redemptionType) {
      errors.push({
        line: lineNumber,
        message: `tipo_resgate inválido: "${tipoRaw}" (use produto, desconto, cortesia ou aniversario).`,
      });
      continue;
    }

    rows.push({
      line: lineNumber,
      payload: {
        uniqueId: sku,
        name: nome,
        value: valor,
        redemption: resgateRaw === 'sim' || resgateRaw === 'true' || resgateRaw === '1',
        redemptionType: redemptionType ?? 'product',
        price: precoRaw ? precoRaw.replace(',', '.') : '0',
      },
    });
  }

  return { rows, errors };
}
